export class GlobalLeaderboard {
  private overlay: HTMLDivElement;
  private tbody!: HTMLTableSectionElement;
  private visible = false;
  private apiBase: string;
  private loading = false;

  constructor(serverUrl: string) {
    // Convert ws:// URL to http:// for REST calls
    this.apiBase = serverUrl.replace(/^ws(s?):\/\//, 'http$1://');
    this.overlay = this.buildDOM();
    document.body.appendChild(this.overlay);
  }

  private buildDOM(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.id = 'global-leaderboard';
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'none',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '60px',
      zIndex: '60',
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(12px)',
    });

    // Click backdrop to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hide();
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position: 'relative',
      background: 'rgba(8,8,18,0.95)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '16px',
      padding: '24px 28px 20px',
      minWidth: '520px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      overflowY: 'auto',
      backdropFilter: 'blur(16px)',
      pointerEvents: 'all',
    });

    const title = document.createElement('h2');
    title.textContent = 'Global Leaderboard';
    Object.assign(title.style, {
      fontSize: '13px',
      fontWeight: '800',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.45)',
      marginBottom: '16px',
      textAlign: 'center',
    });

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u2715';
    Object.assign(closeBtn.style, {
      position: 'absolute',
      top: '16px',
      right: '16px',
      background: 'rgba(255,255,255,0.08)',
      border: 'none',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '18px',
      width: '36px',
      height: '36px',
      cursor: 'pointer',
      pointerEvents: 'all',
    });
    closeBtn.addEventListener('click', () => this.hide());

    const table = document.createElement('table');
    Object.assign(table.style, { width: '100%', borderCollapse: 'collapse' });

    const thStyle = 'padding:0 10px 8px;font-size:10px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase;text-align:left;border-bottom:1px solid rgba(255,255,255,0.06)';
    const thead = document.createElement('thead');
    thead.innerHTML = `<tr>
      <th style="${thStyle};color:rgba(255,255,255,0.3)">#</th>
      <th style="${thStyle};color:rgba(255,255,255,0.3)">Name</th>
      <th style="${thStyle};color:#ff5252">Kills</th>
      <th style="${thStyle};color:rgba(255,255,255,0.3)">Deaths</th>
      <th style="${thStyle};color:rgba(255,255,255,0.3)">K/D</th>
      <th style="${thStyle};color:rgba(255,255,255,0.3)">Best Lvl</th>
    </tr>`;

    this.tbody = document.createElement('tbody');
    table.appendChild(thead);
    table.appendChild(this.tbody);

    panel.appendChild(closeBtn);
    panel.appendChild(title);
    panel.appendChild(table);
    overlay.appendChild(panel);

    document.addEventListener('keydown', (e) => {
      if (e.code === 'Escape' && this.visible) this.hide();
    });

    return overlay;
  }

  toggle() {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.fetchAndRender();
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
  }

  get isOpen() { return this.visible; }

  private async fetchAndRender() {
    if (this.loading) return;
    this.loading = true;
    this.tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:rgba(255,255,255,0.3)">Loading...</td></tr>';

    try {
      const res = await fetch(`${this.apiBase}/api/leaderboard?sort=kills&limit=50`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: Array<{
        name: string;
        totalKills: number;
        totalDeaths: number;
        bestLevel: number;
      }> = await res.json();

      this.tbody.innerHTML = '';

      if (data.length === 0) {
        this.tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:rgba(255,255,255,0.3)">No data yet</td></tr>';
        return;
      }

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const kd = row.totalDeaths === 0
          ? (row.totalKills > 0 ? '\u221e' : '\u2014')
          : (row.totalKills / row.totalDeaths).toFixed(1);
        const nameSafe = row.name.replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const tr = document.createElement('tr');
        tr.style.borderBottom = '1px solid rgba(255,255,255,0.04)';
        tr.innerHTML = `
          <td class="lb-rank">${i + 1}</td>
          <td class="lb-name">${nameSafe}</td>
          <td class="lb-kills">${row.totalKills}</td>
          <td class="lb-deaths">${row.totalDeaths}</td>
          <td class="lb-kd">${kd}</td>
          <td class="lb-level">\u26a1${row.bestLevel}</td>
        `;
        this.tbody.appendChild(tr);
      }
    } catch (err) {
      this.tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:#ff5252">Failed to load leaderboard</td></tr>';
      console.warn('[GlobalLeaderboard]', err);
    } finally {
      this.loading = false;
    }
  }
}
